import { DeployUiState, StoreInfra, StoreWithDeployStatus } from '@/models/operations/types'

import { normalizeOpsDateInput } from './dateUtils'

const BUILDING_STATES = new Set(['BUILDING', 'QUEUED', 'INITIALIZING'])
const FAILED_STATES = new Set(['ERROR', 'CANCELED', 'CANCELLED', 'FAILED', 'BLOCKED'])
const TERMINAL_STATES = new Set(['READY', ...FAILED_STATES])

function normalizeDeploymentStatus(deploymentStatus: string): string {
  return typeof deploymentStatus === 'string' ? deploymentStatus.trim().toUpperCase() : ''
}

/** Map deployment status to UI state */
export function mapDeploymentStatusToUi(deploymentStatus: string): DeployUiState {
  const normalized = normalizeDeploymentStatus(deploymentStatus)

  if (normalized === 'READY') return 'running'
  if (BUILDING_STATES.has(normalized)) return 'building'

  return 'failed'
}

export function isDeploymentStatusTerminal(deploymentStatus: string): boolean {
  return TERMINAL_STATES.has(normalizeDeploymentStatus(deploymentStatus))
}

export function isDeploymentStatusBuilding(deploymentStatus: string): boolean {
  return BUILDING_STATES.has(normalizeDeploymentStatus(deploymentStatus))
}

type StoreInfraInput = Omit<StoreInfra, 'ready'> & { ready?: string | number | null }

/** ISO string for UI formatting, or null when `ready` is absent */
export function getStoreReadyIso(store: Pick<StoreInfra, 'ready'> | null | undefined): string | null {
  if (!store) return null
  return normalizeOpsDateInput(store.ready)
}

export function normalizeStoreInfra(store: StoreInfraInput): StoreInfra {
  return {
    ...store,
    ready: store.ready ?? null,
  }
}

export function mapStoreInfraToOpsStore(store: StoreInfra): StoreWithDeployStatus {
  const normalized = normalizeStoreInfra(store)
  const isActive = normalized.status === true
  return {
    ...normalized,
    deployUiState: mapDeploymentStatusToUi(normalized.deployment_status),
    isActive,
    isChurned: !isActive,
  }
}

export function filterStoresByTab(
  stores: StoreWithDeployStatus[],
  status: 'active' | 'churned',
): StoreWithDeployStatus[] {
  return stores.filter((store) => (status === 'active' ? store.isActive : store.isChurned))
}

export function filterStoresBySearch(
  stores: StoreWithDeployStatus[],
  search: string,
): StoreWithDeployStatus[] {
  const term = search.trim().toLowerCase()
  if (!term) return stores
  return stores.filter((store) => store.name.toLowerCase().includes(term))
}
